import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Globe, Save, X, ExternalLink } from 'lucide-react';
import { domainAPI } from '../../services/api';
import toast from 'react-hot-toast';
import '../Admin/Admin.css';

const DomainManagement = () => {
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  
  const [formData, setFormData] = useState({
    domain: '',
    name: '',
    description: '',
    logo: '',
    favicon: '',
    isActive: true,
    settings: {
      primaryColor: '#8b5cf6',
      secondaryColor: '#f472b6',
      googleAnalyticsId: '',
      facebookPixelId: '',
      customCss: '',
      customJs: ''
    },
    seo: {
      title: '',
      description: '',
      keywords: '',
      ogImage: ''
    },
    contact: {
      email: '',
      facebook: '',
      telegram: '',
      zalo: ''
    }
  });

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setIsLoading(true);
    try {
      const response = await domainAPI.getAll();
      setDomains(response.data || []);
    } catch (error) {
      toast.error('Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      domain: '',
      name: '',
      description: '',
      logo: '',
      favicon: '',
      isActive: true,
      settings: {
        primaryColor: '#8b5cf6',
        secondaryColor: '#f472b6',
        googleAnalyticsId: '',
        facebookPixelId: '',
        customCss: '',
        customJs: ''
      },
      seo: {
        title: '',
        description: '',
        keywords: '',
        ogImage: ''
      },
      contact: {
        email: '',
        facebook: '',
        telegram: '',
        zalo: ''
      }
    });
    setEditingDomain(null);
  };

  const openModal = (domain = null) => {
    if (domain) {
      setEditingDomain(domain);
      setFormData({
        domain: domain.domain || '',
        name: domain.name || '',
        description: domain.description || '',
        logo: domain.logo || '',
        favicon: domain.favicon || '',
        isActive: domain.isActive !== false,
        settings: { ...formData.settings, ...domain.settings },
        seo: { ...formData.seo, ...domain.seo },
        contact: { ...formData.contact, ...domain.contact }
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.domain.trim()) {
      toast.error('Please enter domain name');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Please enter website name');
      return;
    }

    try {
      if (editingDomain) {
        await domainAPI.update(editingDomain._id, formData);
        toast.success('Domain updated successfully');
      } else {
        await domainAPI.create(formData);
        toast.success('Domain created successfully');
      }
      fetchDomains();
      closeModal();
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await domainAPI.delete(id);
      toast.success('Domain deleted');
      fetchDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedFormData = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  return (
    <div className="domain-management">
      <div className="admin-page-header">
        <h1>Domain Management</h1>
        <button className="btn-add" onClick={() => openModal()}>
          <Plus size={18} />
          Add Domain
        </button>
      </div>

      {isLoading ? (
        <div className="admin-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : domains.length > 0 ? (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Website Name</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Globe size={18} style={{ color: 'var(--primary)' }} />
                      <div>
                        <strong>{domain.domain}</strong>
                        <a 
                          href={`https://${domain.domain}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ marginLeft: '8px', color: 'var(--text-muted)' }}
                        >
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    </div>
                  </td>
                  <td>{domain.name}</td>
                  <td>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      background: domain.isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      color: domain.isActive ? '#22c55e' : '#ef4444'
                    }}>
                      {domain.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    {new Date(domain.createdAt).toLocaleDateString('en-US')}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => openModal(domain)}>
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(domain._id, domain.domain)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-empty">
          <Globe size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>No domains found</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDomain ? 'Edit Domain' : 'Add Domain'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-tabs">
                <div className="form-section">
                  <h3>Basic Information</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Domain Name *</label>
                      <input
                        type="text"
                        value={formData.domain}
                        onChange={(e) => updateFormData('domain', e.target.value)}
                        placeholder="example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Website Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        placeholder="My Novel Website"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      placeholder="Website description..."
                      rows={2}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Logo URL</label>
                      <input
                        type="text"
                        value={formData.logo}
                        onChange={(e) => updateFormData('logo', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Favicon URL</label>
                      <input
                        type="text"
                        value={formData.favicon}
                        onChange={(e) => updateFormData('favicon', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-item" style={{ width: 'fit-content' }}>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => updateFormData('isActive', e.target.checked)}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Theme Settings</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Primary Color</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="color"
                          value={formData.settings.primaryColor}
                          onChange={(e) => updateNestedFormData('settings', 'primaryColor', e.target.value)}
                          style={{ width: '50px', height: '38px' }}
                        />
                        <input
                          type="text"
                          value={formData.settings.primaryColor}
                          onChange={(e) => updateNestedFormData('settings', 'primaryColor', e.target.value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Secondary Color</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                          type="color"
                          value={formData.settings.secondaryColor}
                          onChange={(e) => updateNestedFormData('settings', 'secondaryColor', e.target.value)}
                          style={{ width: '50px', height: '38px' }}
                        />
                        <input
                          type="text"
                          value={formData.settings.secondaryColor}
                          onChange={(e) => updateNestedFormData('settings', 'secondaryColor', e.target.value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Google Analytics ID</label>
                      <input
                        type="text"
                        value={formData.settings.googleAnalyticsId}
                        onChange={(e) => updateNestedFormData('settings', 'googleAnalyticsId', e.target.value)}
                        placeholder="G-XXXXXXXXXX"
                      />
                    </div>
                    <div className="form-group">
                      <label>Facebook Pixel ID</label>
                      <input
                        type="text"
                        value={formData.settings.facebookPixelId}
                        onChange={(e) => updateNestedFormData('settings', 'facebookPixelId', e.target.value)}
                        placeholder="XXXXXXXXXXXXXXX"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>SEO</h3>
                  
                  <div className="form-group">
                    <label>SEO Title</label>
                    <input
                      type="text"
                      value={formData.seo.title}
                      onChange={(e) => updateNestedFormData('seo', 'title', e.target.value)}
                      placeholder="Website title for SEO"
                    />
                  </div>

                  <div className="form-group">
                    <label>SEO Description</label>
                    <textarea
                      value={formData.seo.description}
                      onChange={(e) => updateNestedFormData('seo', 'description', e.target.value)}
                      placeholder="Meta description..."
                      rows={2}
                    />
                  </div>

                  <div className="form-group">
                    <label>Keywords</label>
                    <input
                      type="text"
                      value={formData.seo.keywords}
                      onChange={(e) => updateNestedFormData('seo', 'keywords', e.target.value)}
                      placeholder="keyword1, keyword2, ..."
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3>Contact</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={formData.contact.email}
                        onChange={(e) => updateNestedFormData('contact', 'email', e.target.value)}
                        placeholder="contact@example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Facebook</label>
                      <input
                        type="text"
                        value={formData.contact.facebook}
                        onChange={(e) => updateNestedFormData('contact', 'facebook', e.target.value)}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  <Save size={18} />
                  {editingDomain ? 'Update' : 'Create Domain'}
                </button>
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .modal-large {
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-form {
          padding: 24px;
        }
        .form-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }
        .form-section:last-of-type {
          border-bottom: none;
        }
        .form-section h3 {
          font-size: 1rem;
          color: var(--primary);
          margin-bottom: 16px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 640px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default DomainManagement;
